import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, ArrowLeft, Image as ImageIcon, Video, Sparkles, Eye, Download, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';

interface Category {
  id: string;
  name: string;
  products: Product[];
}

interface Product {
  id: string;
  name: string;
  sku: string;
  images: ImageFile[];
  aiImages: ImageFile[];
  aiVideos: VideoFile[];
}

interface ImageFile {
  id: string;
  name: string;
  url: string;
  path: string;
  uploadedAt: string;
  size: number;
  type: 'product' | 'ai';
}

interface VideoFile {
  id: string;
  name: string;
  url: string;
  path: string;
  generatedAt: string;
  size: number;
}

interface AllFilesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AllFilesModal: React.FC<AllFilesModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentView, setCurrentView] = useState<'categories' | 'products' | 'files'>('categories');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedFile, setSelectedFile] = useState<ImageFile | VideoFile | null>(null);
  const [pathHistory, setPathHistory] = useState<string[]>([]);

  // Cargar categorías y productos
  const loadCategories = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      console.log('📁 Cargando todas las categorías:', user.uid);
      
      // Usar el mismo servicio que el resto del sistema
      const optimizedService = (await import('../services/optimizedInventoryService')).default;
      const categoriesData = await optimizedService.getCategories();
      
      console.log(`📊 Categorías obtenidas del servicio: ${categoriesData.length}`);
      
      if (categoriesData.length === 0) {
        setError('No se encontraron categorías. Las categorías deben estar definidas en el sistema.');
        setLoading(false);
        return;
      }
      
      // Convertir las categorías del servicio al formato del modal
      // Mostrar todas las categorías (habilitadas y deshabilitadas)
      console.log(`📊 Mostrando todas las categorías: ${categoriesData.length}`);
      
      const categoriesForModal: Category[] = [];
      
      for (const category of categoriesData) {
        // Obtener productos de esta categoría desde Firestore
        const productsRef = window.auth.collection(window.auth.db, 'users', user.uid, 'categories', category.id, 'stock');
        const productsSnapshot = await window.auth.getDocs(productsRef);
        
        console.log(`📦 Productos en ${category.name}: ${productsSnapshot.docs.length}`);
        
        const products: Product[] = [];
        
        for (const productDoc of productsSnapshot.docs) {
          const productData = productDoc.data();
          const sku = productDoc.id;
          
          // Obtener todos los archivos multimedia de este producto
          const [images, aiImages, aiVideos] = await Promise.all([
            loadProductImages(category.id, sku),
            loadProductAIImages(category.id, sku),
            loadProductAIVideos(category.id, sku)
          ]);
          
          products.push({
            id: sku,
            name: productData.name || `Producto ${sku}`,
            sku: sku,
            images: images,
            aiImages: aiImages,
            aiVideos: aiVideos
          });
        }
        
        categoriesForModal.push({
          id: category.id,
          name: category.name,
          products: products
        });
      }
      
      setCategories(categoriesForModal);
      console.log(`✅ Cargadas ${categoriesForModal.length} categorías con todos los archivos`);
      
    } catch (error) {
      console.error('❌ Error cargando categorías:', error);
      setError('Error al cargar las categorías desde el servicio');
    } finally {
      setLoading(false);
    }
  };

  // Cargar imágenes del producto
  const loadProductImages = async (categoryId: string, sku: string): Promise<ImageFile[]> => {
    if (!user || !window.auth?.storage) return [];

    try {
      const imagesPath = `users/${user.uid}/categories/${categoryId}/stock/${sku}`;
      const imagesRef = window.auth.ref(window.auth.storage, imagesPath);
      const imagesSnapshot = await window.auth.listAll(imagesRef);
      
      const images: ImageFile[] = [];
      
      for (const itemRef of imagesSnapshot.items) {
        try {
          const url = await window.auth.getDownloadURL(itemRef);
          const metadata = await window.auth.getMetadata(itemRef);
          
          // Detectar si es una imagen por content type o por extensión
          const fileName = itemRef.name.toLowerCase();
          const contentType = metadata.contentType || '';
          const hasImageExtension = fileName.endsWith('.jpg') || 
                                   fileName.endsWith('.jpeg') || 
                                   fileName.endsWith('.png') || 
                                   fileName.endsWith('.gif') || 
                                   fileName.endsWith('.webp');
          
          const isImage = (contentType && contentType.startsWith('image/')) || hasImageExtension;
          
          if (isImage) {
            // Excluir imágenes generadas con IA
            const isAIGenerated = fileName.includes('ai') || 
                                 fileName.includes('generated') || 
                                 fileName.includes('modelo') ||
                                 fileName.includes('ia');
            
            if (!isAIGenerated) {
              images.push({
                id: itemRef.name,
                name: itemRef.name,
                url: url,
                path: itemRef.fullPath,
                uploadedAt: metadata.timeCreated || new Date().toISOString(),
                size: metadata.size || 0,
                type: 'product'
              });
            }
          }
        } catch (err) {
          console.warn('⚠️ Error obteniendo URL para imagen:', itemRef.name, err);
        }
      }
      
      return images;
    } catch (error) {
      console.error('❌ Error cargando imágenes del producto:', error);
      return [];
    }
  };

  // Cargar imágenes IA del producto
  const loadProductAIImages = async (categoryId: string, sku: string): Promise<ImageFile[]> => {
    if (!user || !window.auth?.storage) return [];

    try {
      const aiImagesPath = `users/${user.uid}/categories/${categoryId}/stock/${sku}/imgIA`;
      const aiImagesRef = window.auth.ref(window.auth.storage, aiImagesPath);
      const aiImagesSnapshot = await window.auth.listAll(aiImagesRef);
      
      const aiImages: ImageFile[] = [];
      
      for (const itemRef of aiImagesSnapshot.items) {
        try {
          const url = await window.auth.getDownloadURL(itemRef);
          const metadata = await window.auth.getMetadata(itemRef);
          
          // Detectar si es una imagen por content type o por extensión
          const fileName = itemRef.name.toLowerCase();
          const contentType = metadata.contentType || '';
          const hasImageExtension = fileName.endsWith('.jpg') || 
                                   fileName.endsWith('.jpeg') || 
                                   fileName.endsWith('.png') || 
                                   fileName.endsWith('.gif') || 
                                   fileName.endsWith('.webp');
          
          const isImage = (contentType && contentType.startsWith('image/')) || hasImageExtension;
          
          // Si estamos en la carpeta /imgIA, todas las imágenes son generadas con IA
          if (isImage) {
            aiImages.push({
              id: itemRef.name,
              name: itemRef.name,
              url: url,
              path: itemRef.fullPath,
              uploadedAt: metadata.timeCreated || new Date().toISOString(),
              size: metadata.size || 0,
              type: 'ai'
            });
          }
        } catch (err) {
          console.warn('⚠️ Error obteniendo URL para imagen IA:', itemRef.name, err);
        }
      }
      
      return aiImages;
    } catch (error) {
      console.error('❌ Error cargando imágenes IA del producto:', error);
      return [];
    }
  };

  // Cargar videos IA del producto
  const loadProductAIVideos = async (categoryId: string, sku: string): Promise<VideoFile[]> => {
    if (!user || !window.auth?.storage) return [];

    try {
      const aiVideosPath = `users/${user.uid}/categories/${categoryId}/stock/${sku}/vidIA`;
      const aiVideosRef = window.auth.ref(window.auth.storage, aiVideosPath);
      const aiVideosSnapshot = await window.auth.listAll(aiVideosRef);
      
      const aiVideos: VideoFile[] = [];
      
      for (const itemRef of aiVideosSnapshot.items) {
        try {
          const url = await window.auth.getDownloadURL(itemRef);
          const metadata = await window.auth.getMetadata(itemRef);
          
          // Detectar si es un video por content type o por extensión
          const fileName = itemRef.name.toLowerCase();
          const contentType = metadata.contentType || '';
          const hasVideoExtension = fileName.endsWith('.mp4') || 
                                   fileName.endsWith('.mov') || 
                                   fileName.endsWith('.avi') || 
                                   fileName.endsWith('.webm') ||
                                   fileName.endsWith('.mkv');
          
          const isVideo = (contentType && contentType.startsWith('video/')) || hasVideoExtension;
          
          if (isVideo) {
            aiVideos.push({
              id: itemRef.name,
              name: itemRef.name,
              url: url,
              path: itemRef.fullPath,
              generatedAt: metadata.timeCreated || new Date().toISOString(),
              size: metadata.size || 0
            });
          }
        } catch (err) {
          console.warn('⚠️ Error obteniendo URL para video IA:', itemRef.name, err);
        }
      }
      
      return aiVideos;
    } catch (error) {
      console.error('❌ Error cargando videos IA del producto:', error);
      return [];
    }
  };

  // Filtrar categorías por término de búsqueda
  const filteredCategories = categories.filter(category => {
    if (!searchTerm) return true;
    
    const categoryMatch = category.name.toLowerCase().includes(searchTerm.toLowerCase());
    const productMatch = category.products.some(product => 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    return categoryMatch || productMatch;
  });

  // Navegar a productos de una categoría
  const navigateToProducts = (category: Category) => {
    setSelectedCategory(category);
    setCurrentView('products');
    setPathHistory([...pathHistory, 'categories']);
  };

  // Navegar a archivos de un producto
  const navigateToFiles = (product: Product) => {
    setSelectedProduct(product);
    setCurrentView('files');
    setPathHistory([...pathHistory, 'products']);
  };

  // Volver atrás
  const goBack = () => {
    if (pathHistory.length === 0) {
      setCurrentView('categories');
      setSelectedCategory(null);
      setSelectedProduct(null);
    } else if (currentView === 'files') {
      setCurrentView('products');
      setSelectedProduct(null);
      const newHistory = [...pathHistory];
      newHistory.pop();
      setPathHistory(newHistory);
    } else if (currentView === 'products') {
      setCurrentView('categories');
      setSelectedCategory(null);
      const newHistory = [...pathHistory];
      newHistory.pop();
      setPathHistory(newHistory);
    }
  };

  // Abrir archivo en vista ampliada
  const openFilePreview = (file: ImageFile | VideoFile) => {
    setSelectedFile(file);
  };

  // Descargar archivo
  const downloadFile = async (file: ImageFile | VideoFile) => {
    try {
      const response = await fetch(file.url);
      const blob = await response.blob();
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('❌ Error descargando archivo:', error);
    }
  };

  // Eliminar archivo
  const deleteFile = async (file: ImageFile | VideoFile) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este archivo?')) return;

    try {
      const fileRef = window.auth.storage.ref(file.path);
      await window.auth.storage.deleteObject(fileRef);
      
      // Recargar datos
      await loadCategories();
      
    } catch (error) {
      console.error('❌ Error eliminando archivo:', error);
      alert('Error al eliminar el archivo');
    }
  };

  // Formatear tamaño de archivo
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Cargar datos al abrir el modal
  useEffect(() => {
    if (isOpen) {
      loadCategories();
      setCurrentView('categories');
      setSelectedCategory(null);
      setSelectedProduct(null);
      setSelectedFile(null);
      setPathHistory([]);
      setSearchTerm('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-4">
              {currentView !== 'categories' && (
                <button
                  onClick={goBack}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Todo mi archivo
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {currentView === 'categories' && 'Selecciona una categoría'}
                  {currentView === 'products' && selectedCategory && `Productos en ${selectedCategory.name}`}
                  {currentView === 'files' && selectedProduct && `Archivos de ${selectedProduct.name}`}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por nombre de producto o SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {loading && (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                <span className="ml-2 text-gray-600 dark:text-gray-300">Cargando...</span>
              </div>
            )}

            {error && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-red-500 text-lg mb-2">❌</div>
                  <p className="text-red-600 dark:text-red-400">{error}</p>
                  <button
                    onClick={loadCategories}
                    className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    Reintentar
                  </button>
                </div>
              </div>
            )}

            {!loading && !error && (
              <div className="h-full overflow-y-auto p-6">
                {currentView === 'categories' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredCategories.map((category) => (
                      <motion.div
                        key={category.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        onClick={() => navigateToProducts(category)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                            <ImageIcon className="w-6 h-6 text-blue-500" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900 dark:text-gray-100">
                              {category.name}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {category.products.length} productos
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}

                {currentView === 'products' && selectedCategory && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {selectedCategory.products.map((product) => (
                      <motion.div
                        key={product.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        onClick={() => navigateToFiles(product)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                            <span className="text-green-500 text-lg">📦</span>
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900 dark:text-gray-100">
                              {product.name}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              SKU: {product.sku}
                            </p>
                            <div className="flex space-x-2 text-xs text-gray-500 dark:text-gray-400 mt-1">
                              <span>{product.images.length} imágenes</span>
                              <span>•</span>
                              <span>{product.aiImages.length} IA</span>
                              <span>•</span>
                              <span>{product.aiVideos.length} videos</span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}

                {currentView === 'files' && selectedProduct && (
                  <div className="space-y-8">
                    {/* Imágenes del producto */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center space-x-2">
                        <ImageIcon className="w-5 h-5 text-blue-500" />
                        <span>🖼️ Imágenes del producto</span>
                      </h3>
                      {selectedProduct.images.length === 0 ? (
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 text-center">
                          <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500 dark:text-gray-400">No hay imágenes cargadas para este producto</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                          {selectedProduct.images.map((image) => (
                            <motion.div
                              key={image.id}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              className="relative group bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden"
                            >
                              <div className="aspect-square relative">
                                <img
                                  src={image.url}
                                  alt={image.name}
                                  className="w-full h-full object-cover"
                                  onClick={() => openFilePreview(image)}
                                />
                                
                                {/* Overlay de acciones */}
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
                                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openFilePreview(image);
                                      }}
                                      className="p-1 bg-white rounded-full hover:bg-gray-100 transition-colors"
                                    >
                                      <Eye className="w-3 h-3 text-gray-700" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        downloadFile(image);
                                      }}
                                      className="p-1 bg-white rounded-full hover:bg-gray-100 transition-colors"
                                    >
                                      <Download className="w-3 h-3 text-gray-700" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteFile(image);
                                      }}
                                      className="p-1 bg-white rounded-full hover:bg-gray-100 transition-colors"
                                    >
                                      <Trash2 className="w-3 h-3 text-red-600" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Modelos IA */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center space-x-2">
                        <Sparkles className="w-5 h-5 text-purple-500" />
                        <span>🤖 Modelos IA</span>
                      </h3>
                      {selectedProduct.aiImages.length === 0 ? (
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 text-center">
                          <Sparkles className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500 dark:text-gray-400">No hay imágenes generadas con IA</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                          {selectedProduct.aiImages.map((image) => (
                            <motion.div
                              key={image.id}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              className="relative group bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden"
                            >
                              <div className="aspect-square relative">
                                <img
                                  src={image.url}
                                  alt={image.name}
                                  className="w-full h-full object-cover"
                                  onClick={() => openFilePreview(image)}
                                />
                                
                                {/* Badge de IA */}
                                <div className="absolute top-1 left-1 bg-purple-500 text-white text-xs px-1 py-0.5 rounded-full">
                                  IA
                                </div>
                                
                                {/* Overlay de acciones */}
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
                                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openFilePreview(image);
                                      }}
                                      className="p-1 bg-white rounded-full hover:bg-gray-100 transition-colors"
                                    >
                                      <Eye className="w-3 h-3 text-gray-700" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        downloadFile(image);
                                      }}
                                      className="p-1 bg-white rounded-full hover:bg-gray-100 transition-colors"
                                    >
                                      <Download className="w-3 h-3 text-gray-700" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteFile(image);
                                      }}
                                      className="p-1 bg-white rounded-full hover:bg-gray-100 transition-colors"
                                    >
                                      <Trash2 className="w-3 h-3 text-red-600" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Videos IA */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center space-x-2">
                        <Video className="w-5 h-5 text-red-500" />
                        <span>🎬 Videos IA</span>
                      </h3>
                      {selectedProduct.aiVideos.length === 0 ? (
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 text-center">
                          <Video className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500 dark:text-gray-400">No hay videos generados con IA</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {selectedProduct.aiVideos.map((video) => (
                            <motion.div
                              key={video.id}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              className="relative group bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden"
                            >
                              <div className="aspect-video relative">
                                <video
                                  src={video.url}
                                  className="w-full h-full object-cover"
                                  poster=""
                                  preload="metadata"
                                />
                                
                                {/* Badge de IA */}
                                <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                                  IA
                                </div>
                                
                                {/* Botón de play */}
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openFilePreview(video);
                                    }}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white bg-opacity-90 rounded-full p-2 hover:bg-opacity-100"
                                  >
                                    <span className="text-gray-700 text-lg">▶</span>
                                  </button>
                                </div>
                                
                                {/* Overlay de acciones */}
                                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadFile(video);
                                    }}
                                    className="p-1 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100 transition-colors"
                                  >
                                    <Download className="w-3 h-3 text-gray-700" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteFile(video);
                                    }}
                                    className="p-1 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100 transition-colors"
                                  >
                                    <Trash2 className="w-3 h-3 text-red-600" />
                                  </button>
                                </div>
                              </div>
                              
                              <div className="p-3">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate mb-1">
                                  {video.name}
                                </p>
                                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                                  <span>{new Date(video.generatedAt).toLocaleDateString()}</span>
                                  <span>{formatFileSize(video.size)}</span>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* Modal de vista ampliada */}
        <AnimatePresence>
          {selectedFile && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-60 p-4"
              onClick={() => setSelectedFile(null)}
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                className="relative max-w-4xl max-h-[90vh] w-full"
                onClick={(e) => e.stopPropagation()}
              >
                {selectedFile && 'generatedAt' in selectedFile ? (
                  // Es un video
                  <video
                    src={selectedFile.url}
                    controls
                    autoPlay
                    className="w-full h-full max-h-[80vh] object-contain rounded-lg"
                  />
                ) : (
                  // Es una imagen
                  <img
                    src={selectedFile.url}
                    alt={selectedFile.name}
                    className="max-w-full max-h-full object-contain rounded-lg"
                  />
                )}
                <button
                  onClick={() => setSelectedFile(null)}
                  className="absolute top-4 right-4 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

export default AllFilesModal;
